/*
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 UNESP Universidade Estadual Paulista "Júlio de Mesquita Filho"
 * Copyright 2024-2026 UNICAMP Universidade Estadual de Campinas
 *
 */

import { ClassificacaoInscricao } from '../../../enums/classificacao-inscricao'
import { AfterViewInit, Component, OnInit, ViewChild, DestroyRef, inject } from '@angular/core'
import { MatTableDataSource } from '@angular/material/table'
import { MatSort } from '@angular/material/sort'
import { fromEvent, debounceTime, distinctUntilChanged, tap } from 'rxjs'
import { filter } from 'rxjs/operators'
import { ListagensDiversasService } from '../../../services/listagens-diversas.service'
import { ActivatedRoute, Router } from '@angular/router'
import { ListagemClassificacao } from 'src/app/models/listagem-classificacao'
import { DatePipe } from '@angular/common'
import { UnespCoreAuthService, UnespCoreMessageService } from 'src/libs/unesp-core'
import { MatDialog } from '@angular/material/dialog'
import { SigadService } from 'src/app/services/sigad.service'
import { SigadPreviewDialogComponent } from '../../sigad-preview-dialog/sigad-preview-dialog.component'
import { ConcursoService } from 'src/app/services/concurso.service'
import { AndamentoService } from 'src/app/services/andamento.service'
import { SearchInputComponent, DefaultPaginatorComponent } from 'src/app/modules/shared'
import { MaskUtils } from 'src/app/utils/mask.utils'
import { ReporteIdentificacaoService } from 'src/app/services/reporte-identificacao.service'
import { ReporteHistoricoDialogComponent } from '../../comissao-julgadora/reporte-historico-dialog/reporte-historico-dialog.component'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

@Component({
  selector: 'app-classificacao-inscricao',
  templateUrl: './classificacao-inscricao.component.html',
  styleUrls: ['./classificacao-inscricao.component.scss'],
})
export class ClassificacaoInscricaoComponent implements AfterViewInit, OnInit {
  private destroyRef = inject(DestroyRef)

  @ViewChild('paginator', { static: true }) paginator!: DefaultPaginatorComponent
  @ViewChild(MatSort) sort!: MatSort
  @ViewChild('searchInput') searchInput!: SearchInputComponent

  totalElements: number = 0
  sortField = 'classificacaoPosicao'
  sortDirection = 'asc'

  colunasGrid: string[] = ['nome', 'cpf', 'situacao', 'classificacao', 'posicao', 'autor', 'data', 'reporte']
  colunasPcd: string[] = ['nome', 'cpf', 'situacao', 'classificacao', 'posicao', 'autor', 'data', 'reporte']
  dataSource: MatTableDataSource<ListagemClassificacao> = new MatTableDataSource()
  dataSourcePcd: MatTableDataSource<ListagemClassificacao> = new MatTableDataSource()
  mostrarColunaPago: boolean = false

  idConcurso?: number
  concursoTitulo?: string
  filtro: string = ''
  fase1Consolidada = false
  fase2Consolidada = false
  retificado: boolean = false
  reportesVisiveis: any[] = []
  sigadIndisponivel = false
  sigadMensagem: string | null = null

  datepipe: DatePipe = new DatePipe('pt-BR')

  optClassificacao = ClassificacaoInscricao

  constructor(
    private listagensDiversasService: ListagensDiversasService,
    private route: ActivatedRoute,
    private unespCoreMessageService: UnespCoreMessageService,
    private readonly concursoService: ConcursoService,
    private dialog: MatDialog,
    private sigadService: SigadService,
    private unespCoreAuthService: UnespCoreAuthService,
    private andamentoService: AndamentoService,
    private reporteIdentificacaoService: ReporteIdentificacaoService
  ) {}

  ngOnInit(): void {
    this.paginator.pageSize = 10
    ;(this.route.parent?.params || this.route.params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      this.idConcurso = params['id']
      this.getData()
      this.andamentoService.possuiRecursoDeferidoResultadoFinal(this.idConcurso!).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(r => this.retificado = r)
      this.reporteIdentificacaoService.listarVisiveis(this.idConcurso!).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
        data => this.reportesVisiveis = data
      )
    })
  }

  ngAfterViewInit() {
    // server-side search
    fromEvent<KeyboardEvent>(this.searchInput.inputRef.nativeElement, 'keyup')
      .pipe(
        debounceTime(150),
        filter((e: KeyboardEvent) => e.keyCode === 13),
        distinctUntilChanged(),
        tap(() => {
          this.paginator.pageIndex = 0
          this.getData()
        })
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe()

    // on sort change, reset to first page and reload
    this.sort.sortChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.sortField = this.sort.active
      this.sortDirection = this.sort.direction || 'asc'
      this.paginator.pageIndex = 0
      this.getData()
    })

    // on paginate events, load a new page
    this.paginator?.page.pipe(tap(() => this.getData()), takeUntilDestroyed(this.destroyRef)).subscribe()
  }



  private getData() {
    this.listagensDiversasService
      .getClassificacaoInscricao(
        this.idConcurso!,
        this.searchInput?.value,
        this.filtro,
        this.paginator.pageIndex,
        this.paginator.pageSize,
        this.sortField,
        this.sortDirection
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data => {
        let inscricoes = data['content'] as ListagemClassificacao[]

        // Verifica se algum candidato tem valor diferente de 0 para mostrar coluna pago
        this.mostrarColunaPago = inscricoes.some(inscricao => (inscricao as any).valor !== 0)

        // Atualiza colunas baseado na necessidade de mostrar coluna pago
        this.colunasGrid = ['nome', 'cpf', 'situacao']
        this.colunasPcd = ['nome', 'cpf', 'situacao']
        if (this.mostrarColunaPago) {
          this.colunasGrid.push('pago')
          this.colunasPcd.push('pago')
        }
        this.colunasGrid.push('classificacao', 'motivo', 'posicao', 'autor', 'data', 'reporte')
        this.colunasPcd.push('classificacao', 'posicao', 'autor', 'data', 'reporte')

        this.dataSource = new MatTableDataSource(inscricoes)
        this.totalElements = data['totalElements']

        // Filtrar candidatos PCD
        const candidatosPcd = inscricoes.filter(inscricao => (inscricao as any).pcd !== null)
          .map(inscricao => {
            const pcdData = (inscricao as any).pcd
            const inscricaoProcessada = {
              ...inscricao
            }
            return {
              ...inscricaoProcessada,
              posicao: pcdData.posicao,
              data: pcdData.data,
              autor: pcdData.autor
            }
          })
          .sort((a, b) => (a.posicao ?? Infinity) - (b.posicao ?? Infinity))

        this.dataSourcePcd = new MatTableDataSource(candidatosPcd)
      })
    this.sigadService.getProtocolo(this.idConcurso!).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: ({ content, meta }) => {
      this.fase1Consolidada = meta.statusResultadoFase1?.classificacaoConsolidada === true
      this.fase2Consolidada = meta.statusResultadoFase2?.classificacaoConsolidada === true
      // Se protocolo retornou com sucesso e SIGAD não está indisponível, sobrepõe probe falso-positivo
      if (!meta.sigadIndisponivel && content) {
        this.sigadIndisponivel = false
        this.sigadMensagem = null
      } else if (meta.sigadIndisponivel) {
        this.sigadIndisponivel = true
        this.sigadMensagem = 'O SIGAD está temporariamente indisponível. Algumas operações podem não funcionar até que o serviço seja restabelecido.'
      }
    },
      error: () => { this.fase1Consolidada = false; this.fase2Consolidada = false },
    })
    this.concursoService.getTitulo(this.idConcurso).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(resp => {
        this.concursoTitulo = resp.concursoTitulo
      })
  }

  getReportesPorInscricao(inscricaoId: number): any[] {
    return this.reportesVisiveis.filter(r => r.inscricaoId === inscricaoId)
  }

  getReporteIconStyle(inscricaoId: number): string {
    const reportes = this.getReportesPorInscricao(inscricaoId)
    if (!reportes.length) return ''
    return reportes.some(r => r.status === 'PENDENTE') ? '' : 'color: #2e7d32'
  }

  getReporteIconColor(inscricaoId: number): string {
    const reportes = this.getReportesPorInscricao(inscricaoId)
    if (!reportes.length) return ''
    return reportes.some(r => r.status === 'PENDENTE') ? 'warn' : ''
  }

  abrirHistoricoReporte(inscricaoId: number): void {
    const reportes = this.getReportesPorInscricao(inscricaoId)
    if (!reportes.length) return
    this.dialog.open(ReporteHistoricoDialogComponent, {
      data: reportes,
      width: '60vw',
      maxWidth: '90vw',
      autoFocus: false,
    })
  }

  verResultadoFase1(): void {
    this.dialog.open(SigadPreviewDialogComponent, {
      width: '90vw',
      height: '90vh',
      panelClass: 'fullheight-dialog',
      data: {
        titulo: 'Resultado da Fase 1',
        urlPdf: this.sigadService.getClassificacaoFase1PdfUrl(this.idConcurso!),
        mostrarEnvio: false,
      },
    })
  }

  verResultadoFase2(): void {
    this.dialog.open(SigadPreviewDialogComponent, {
      width: '90vw',
      height: '90vh',
      panelClass: 'fullheight-dialog',
      data: {
        titulo: 'Resultado da Fase 2',
        urlPdf: this.sigadService.getClassificacaoFase2PdfUrl(this.idConcurso!),
        mostrarEnvio: false,
      },
    })
  }

  exportar() {
    this.listagensDiversasService
      .getClassificacaoInscricao(this.idConcurso!, this.searchInput?.value, this.filtro, 0, this.totalElements)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data => {
        let recursos: ListagemClassificacao[] = data['content']
        // Cabeçalho dinâmico baseado na exibição da coluna pago
        let header = `"#","NOME","CPF"`
        if (this.mostrarColunaPago) {
          header += `,"PAGO"`
        }
        header += `,"CLASSIFICAÇÃO","POSIÇÃO","MOTIVO","AUTOR","DATA"\r\n`
        let content = header

        recursos.forEach(row => {
          let linha = `"${row.id}","${MaskUtils.preferSocialName(row.nome, row.nomeSocial)}","${MaskUtils.maskCpf(row.cpf)}"`
          if (this.mostrarColunaPago) {
            linha += `,"${row.pago ? 'Sim' : 'Não'}"`
          }
          linha += `,"${row.classificacao}","${row.posicao ?? ''}","${row.motivo ?? ''}","${row.autor ?? ''}","${
            row.data === null ? '' : this.datepipe.transform(row.data, 'dd/MM/YYYY HH:mm:ss')
          }"\r\n`
          content += linha
        })
        let binaryData = []
        binaryData.push(content)
        let downloadLink = document.createElement('a')
        downloadLink.setAttribute('download', `listagem/classificacao-${this.idConcurso}.csv`)
        downloadLink.href = window.URL.createObjectURL(new Blob(binaryData, { type: 'attachment/csvcharset=utf-8' }))
        document.body.appendChild(downloadLink)
        downloadLink.click()
        downloadLink.remove()
      })
  }

  selecionar(): void {
    this.getData()
  }

  isSuperAdmin(): boolean {
    return this.unespCoreAuthService.hasPermissionBasedRoles(['SUPERADMIN'])
  }

}
